/**
 * Hotkeys Setup Utility
 * 
 * Configures and manages all keyboard shortcuts for the shipments page.
 * Uses hotkeys-js library bundled via webpack.
 * 
 * Scopes:
 * - 'all': Global hotkeys (work everywhere except inputs)
 * - 'list': Table view hotkeys
 * - 'modal': Shipment detail modal hotkeys
 * - 'delivery': Delivery modal hotkeys
 */

import hotkeys from 'hotkeys-js';

type ModalWindow = Window & {
  JidokaModal?: {
    show: (target: Element | string) => void;
    hide: (target: Element | string) => void;
  };
  JidokaEditWaypointModal?: {
    isOpen?: () => boolean;
  };
};

function isEditWaypointModalOpen(): boolean {
  return Boolean((window as ModalWindow).JidokaEditWaypointModal?.isOpen?.());
}

// Declare jQuery as global (loaded via CDN in HTML)
declare const $: any;

/**
 * Configure hotkeys filter to ignore input fields by default
 * but allow specific keys like '/' for search
 */
export function configureHotkeysFilter(): void {
  hotkeys.filter = function (event: KeyboardEvent): boolean {
    if (isEditWaypointModalOpen()) return false;

    const target = event.target as HTMLElement;
    const tagName = target.tagName;
    const isInput = tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA';
    const isContentEditable = target.isContentEditable;

    // Allow '/' to work even when not in inputs (for search focus)
    if (event.key === '/' && !isInput) return true;

    // Block hotkeys when typing in input fields
    return !(isInput || isContentEditable);
  };
}

/**
 * Setup global hotkeys (work everywhere except in input fields)
 */
function setupGlobalHotkeys(): void {
  // Search focus - works from anywhere
  hotkeys('/', 'all', function (event) {
    event.preventDefault();
    const searchInput = document.getElementById('tblsearch') as HTMLInputElement;
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
    }
  });

  // Advanced search modal - Cmd+K / Ctrl+K
  hotkeys('cmd+k,ctrl+k', 'all', function (event) {
    event.preventDefault();
    const advancedSearchModal = document.getElementById('advancedSearchModal');
    const modalService = (window as ModalWindow).JidokaModal;
    if (advancedSearchModal && modalService) {
      modalService.show(advancedSearchModal);
      
      // Focus the search input after modal opens
      setTimeout(() => {
        const searchInput = document.getElementById('advancedSearchInput') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  });

  // Show keyboard shortcuts help
  hotkeys('shift+/', 'all', function (event) {
    event.preventDefault();
    const shortcutsModal = document.getElementById('shortcutsModal');
    const modalService = (window as ModalWindow).JidokaModal;
    if (shortcutsModal && modalService) {
      modalService.show(shortcutsModal);
    }
  });

  // Tab switching shortcuts
  hotkeys('shift+i', 'all', function (event) {
    event.preventDefault();
    const tab1Link = document.querySelector('a[href="#tab1"]') as HTMLElement;
    if (tab1Link) tab1Link.click();
  });

  hotkeys('shift+d', 'all', function (event) {
    event.preventDefault();
    const tab2Link = document.querySelector('a[href="#tab2"]') as HTMLElement;
    if (tab2Link) tab2Link.click();
  });
}

/**
 * Setup list scope hotkeys (table view)
 */
function setupListHotkeys(): void {
  // Navigate table rows
  hotkeys('down', 'list', function (event) {
    event.preventDefault();
    (window as any).navigateTable?.('down');
  });

  hotkeys('up', 'list', function (event) {
    event.preventDefault();
    (window as any).navigateTable?.('up');
  });

  // Table pagination
  hotkeys('left', 'list', function (event) {
    event.preventDefault();
    const activeTab = document.querySelector('.material-tabs a.active')?.getAttribute('href');
    
    if (activeTab === '#tab1') {
      const table = ($('#shippinglist') as any).DataTable();
      table.page('previous').draw('page');
    } else if (activeTab === '#tab2' && ($.fn.DataTable as any).isDataTable('#deliveredlist')) {
      const table = ($('#deliveredlist') as any).DataTable();
      table.page('previous').draw('page');
    }
  });

  hotkeys('right', 'list', function (event) {
    event.preventDefault();
    const activeTab = document.querySelector('.material-tabs a.active')?.getAttribute('href');
    
    if (activeTab === '#tab1') {
      const table = ($('#shippinglist') as any).DataTable();
      table.page('next').draw('page');
    } else if (activeTab === '#tab2' && ($.fn.DataTable as any).isDataTable('#deliveredlist')) {
      const table = ($('#deliveredlist') as any).DataTable();
      table.page('next').draw('page');
    }
  });

  // Open focused row
  hotkeys('enter', 'list', function (event) {
    // Ignore key repeat to prevent opening multiple modals
    if (event.repeat) return;
    
    let focusedRow = document.querySelector('#shippinglist tbody tr.focused');
    if (!focusedRow) {
      focusedRow = document.querySelector('#deliveredlist tbody tr.focused');
    }
    if (focusedRow) {
      event.preventDefault();
      const link = focusedRow.querySelector('a') as HTMLElement;
      if (link) link.click();
    }
  });

  // Clear row focus
  hotkeys('esc', 'list', function (event) {
    event.preventDefault();
    const focusedRow = document.querySelector('#shippinglist tbody tr.focused, #deliveredlist tbody tr.focused');
    if (focusedRow) {
      focusedRow.classList.remove('focused');
    }
  });
}

/**
 * Setup modal scope hotkeys (shipment detail view)
 */
function setupModalHotkeys(): void {
  hotkeys('left,right,r,R,b,B,1,2,3,d,D,c,C,s,S,n,N,esc', 'modal', function (event, handler) {
    if (isEditWaypointModalOpen()) return;

    // Ignore key repeat events to prevent multiple actions from holding key
    if (event.repeat) return;
    
    event.preventDefault();

    switch (handler.key.toLowerCase()) {
      case 'left':
        const prevBtn = document.querySelector('.action-buttons.previous') as HTMLElement;
        if (prevBtn && !prevBtn.classList.contains('disabled')) {
          prevBtn.click();
        }
        break;
      
      case 'right':
        const nextBtn = document.querySelector('.action-buttons.next') as HTMLElement;
        if (nextBtn && !nextBtn.classList.contains('disabled')) {
          nextBtn.click();
        }
        break;
      
      case 'r':
        const refreshBtn = document.querySelector('.action-buttons.refresh') as HTMLElement;
        if (refreshBtn && !refreshBtn.classList.contains('disabled')) {
          refreshBtn.click();
        }
        break;
      
      case 'b':
        const bookmarkBtn = document.querySelector('.action-buttons.bookmark') as HTMLElement;
        if (bookmarkBtn) bookmarkBtn.click();
        break;
      
      case '1':
        const tab1Sub = document.querySelector('a[href="#tab1-sub"]') as HTMLElement;
        if (tab1Sub) tab1Sub.click();
        break;
      
      case '2':
        const tab3Sub = document.querySelector('a[href="#tab3-sub"]') as HTMLElement;
        if (tab3Sub) tab3Sub.click();
        break;
      
      case '3':
        const tab4Sub = document.querySelector('a[href="#tab4-sub"]') as HTMLElement;
        if (tab4Sub) tab4Sub.click();
        break;
      
      case 'd':
        // Click the Mark as Delivered button in the action menu
        const deliverBtnMenu = document.getElementById('deliverBtnMenu') as HTMLElement;
        if (deliverBtnMenu) deliverBtnMenu.click();
        break;
      
      case 'c':
        const noteInput = document.getElementById('text') as HTMLInputElement;
        if (noteInput) noteInput.focus();
        break;
      
      case 's':
        // Open Shopify link if available
        const shopifyLink = document.querySelector('.action-menu-dropdown a[href*="shopify"]') as HTMLElement;
        if (shopifyLink) shopifyLink.click();
        break;
      
      case 'n':
        // Open NetSuite link if available
        const netsuiteLink = document.querySelector('.action-menu-dropdown a[href*="netsuite"]') as HTMLElement;
        if (netsuiteLink) netsuiteLink.click();
        break;
      
      case 'esc':
      const mainModal = document.getElementById('modal');
      const modalService = (window as ModalWindow).JidokaModal;
      if (mainModal && modalService) {
        modalService.hide(mainModal);
        }
        break;
    }
  });
}

/**
 * Setup delivery modal hotkeys
 */
function setupDeliveryModalHotkeys(): void {
  hotkeys('esc', 'delivery', function (event) {
    event.preventDefault();
      const deliveryModal = document.getElementById('deliveryModal');
      const modalService = (window as ModalWindow).JidokaModal;
      if (deliveryModal && modalService) {
        modalService.hide(deliveryModal);
    }
  });
}

/**
 * Setup modal event listeners to switch scopes
 */
function setupModalScopeListeners(): void {
  const mainModal = document.querySelector('#modal');
  const deliveryModal = document.querySelector('#deliveryModal');

  if (mainModal) {
    // Switch to modal scope when modal opens
    mainModal.addEventListener('show.bs.modal', () => {
      hotkeys.setScope('modal');
    });

    // Switch back to list scope when modal closes
    mainModal.addEventListener('hidden.bs.modal', () => {
      hotkeys.setScope('list');
    });
  }

  if (deliveryModal) {
    // Disable hotkeys in delivery modal (except Esc)
    deliveryModal.addEventListener('show.bs.modal', () => {
      hotkeys.setScope('delivery');
    });

    // Switch back to modal scope when delivery modal closes
    deliveryModal.addEventListener('hidden.bs.modal', () => {
      hotkeys.setScope('modal');
    });
  }
}

/**
 * Setup search input escape key handler
 * Clears the search and blurs the input when Escape is pressed
 */
function setupSearchInputEscapeHandler(): void {
  const searchInput = document.getElementById('tblsearch') as HTMLInputElement;
  if (!searchInput) return;

  searchInput.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Clear the search value
      searchInput.value = '';
      // Trigger search clear event to reset table
      const searchEvent = new Event('search', { bubbles: true });
      searchInput.dispatchEvent(searchEvent);
      // Blur the input and focus document
      searchInput.blur();
      document.body.focus();
    }
  });
}

/**
 * Initialize all keyboard shortcuts
 * Call this function once on page load after DOM is ready
 */
export function setupHotkeys(): void {
  // Configure filter
  configureHotkeysFilter();

  // Set initial scope to 'list' (table view)
  hotkeys.setScope('list');

  // Setup all hotkey scopes
  setupGlobalHotkeys();
  setupListHotkeys();
  setupModalHotkeys();
  setupDeliveryModalHotkeys();
  setupModalScopeListeners();
  
  // Setup search input special handling
  setupSearchInputEscapeHandler();

  // console.log('[Hotkeys] Keyboard shortcuts initialized');
}

/**
 * Cleanup function to unbind all hotkeys
 * Useful for testing or if you need to reinitialize
 */
export function cleanupHotkeys(): void {
  hotkeys.unbind();
  console.log('[Hotkeys] All keyboard shortcuts removed');
}

/**
 * Get current hotkey scope
 */
export function getCurrentScope(): string {
  return hotkeys.getScope();
}

/**
 * Set hotkey scope programmatically
 */
export function setScope(scope: 'all' | 'list' | 'modal' | 'delivery'): void {
  hotkeys.setScope(scope);
}
